"use client";

import { motion, type Variants } from "framer-motion";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
export interface FAQItem {
	question: string;
	answer: string;
}

const SECTION_CONTENT = {
	title: "Frequently Asked Questions",
	subtitle: "Everything you need to know about the product and billing.",
} as const;

const fadeIn: Variants = {
	hidden: { opacity: 0, y: 30 },
	visible: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.6, ease: "easeOut" },
	},
};

const accordionFadeIn: Variants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: { duration: 0.8, delay: 0.2 },
	},
};

export function FaqSection({ faqData = [] }: { faqData?: FAQItem[] }) {
	if (!faqData || faqData.length === 0) return null;

	return (
		<section id="faq" className="py-16 md:py-24 bg-zinc-50 dark:bg-zinc-900/50">
			<div className="container mx-auto px-4 max-w-4xl">
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, margin: "-100px" }}
					variants={fadeIn}
					className="text-center mb-16"
				>
					<h2 className="text-3xl md:text-5xl font-black mb-4">
						{SECTION_CONTENT.title}
					</h2>
					<p className="text-lg text-muted-foreground">
						{SECTION_CONTENT.subtitle}
					</p>
				</motion.div>

				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, margin: "-50px" }}
					variants={accordionFadeIn}
				>
					<Accordion type="single" collapsible className="w-full">
						{faqData.map((faq, index) => (
							<AccordionItem
								// biome-ignore lint/suspicious/noArrayIndexKey: Static FAQ data
								key={index}
								value={`item-${index}`}
								className="border-b border-gray-200 dark:border-gray-800"
							>
								<AccordionTrigger className="text-left font-bold text-lg py-6 hover:text-primary transition-colors">
									{faq.question}
								</AccordionTrigger>
								<AccordionContent className="text-muted-foreground text-base leading-relaxed pb-6">
									{faq.answer.split("\n").map((line, i) => (
										// biome-ignore lint/suspicious/noArrayIndexKey: Static lines
										<p key={i} className="mb-4 last:mb-0">
											{line}
										</p>
									))}
								</AccordionContent>
							</AccordionItem>
						))}
					</Accordion>
				</motion.div>
			</div>
		</section>
	);
}
